import React, { useEffect, useState } from 'react';
import { api } from '../../lib/api';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { FileText, Download, Loader2, Calendar, TrendingUp, TrendingDown } from 'lucide-react';
import { toast } from 'sonner';
import { useLanguage } from '../../lib/LanguageContext';

const MONTHS_FR = ['Janvier', 'Février', 'Mars', 'Avril', 'Mai', 'Juin', 'Juillet', 'Août', 'Septembre', 'Octobre', 'Novembre', 'Décembre'];

export default function Releves() {
  const { t } = useLanguage();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/client/statements');
      const list = Array.isArray(data) ? data : (data?.items || data?.statements || []);
      // Sort desc by year/month
      list.sort((a, b) => (b.year - a.year) || (b.month - a.month));
      setItems(list);
    } catch (e) {
      toast.error(t('statements.loadError'));
    } finally {
      setLoading(false);
    }
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, []);

  const download = async (item) => {
    const key = `${item.year}-${item.month}`;
    setDownloading(key);
    try {
      const res = await api.get(`/client/statements/${item.year}/${item.month}/download`, { responseType: 'blob' });
      const blob = new Blob([res.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `releve-bfi-${item.year}-${String(item.month).padStart(2, '0')}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => window.URL.revokeObjectURL(url), 5000);
      toast.success(t('statements.downloaded'));
    } catch (e) {
      toast.error(e?.response?.data?.detail || t('statements.downloadError'));
    } finally {
      setDownloading(null);
    }
  };

  return (
    <div className="space-y-5">
      <div>
        <h1 className="text-2xl font-bold flex items-center gap-2"><FileText className="w-6 h-6 text-blue-700"/> {t('statements.monthly')}</h1>
        <p className="text-slate-500 text-sm mt-1 dark:text-slate-400">{t('statements.monthlyDesc')}</p>
      </div>

      <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden dark:bg-slate-900 dark:border-slate-700">
        {loading ? (
          <div className="p-12 text-center text-slate-500"><Loader2 className="w-5 h-5 animate-spin mx-auto"/></div>
        ) : items.length === 0 ? (
          <div className="p-12 text-center">
            <div className="w-14 h-14 mx-auto rounded-full bg-blue-50 text-blue-700 flex items-center justify-center mb-3"><Calendar className="w-7 h-7"/></div>
            <h2 className="font-semibold">{t('statements.noStatements')}</h2>
            <p className="text-slate-500 text-sm mt-1 dark:text-slate-400">{t('statements.noStatementsDesc')}</p>
          </div>
        ) : (
          <ul className="divide-y divide-slate-100 dark:divide-slate-700">
            {items.map((it) => {
              const key = `${it.year}-${it.month}`;
              const monthLabel = MONTHS_FR[(it.month || 1) - 1] || '';
              return (
                <li key={key} className="p-4 flex items-center gap-4 flex-wrap hover:bg-slate-50 dark:hover:bg-slate-800/40 transition">
                  <div className="w-12 h-12 rounded-xl bg-blue-50 dark:bg-blue-950/40 text-blue-700 flex flex-col items-center justify-center text-xs">
                    <FileText className="w-4 h-4"/>
                    <span className="font-semibold mt-0.5">{String(it.month).padStart(2, '0')}</span>
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-medium">{monthLabel} {it.year}</div>
                    <div className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">
                      {typeof it.transactions_count === 'number' && <span>{it.transactions_count} {it.transactions_count > 1 ? t('dashboard.operations') : t('dashboard.operations').replace(/s$/, '')}</span>}
                      {typeof it.income === 'number' && (
                        <span className="ml-2 inline-flex items-center gap-1 text-emerald-600 dark:text-emerald-400">
                          <TrendingUp className="w-3 h-3"/> +{it.income.toLocaleString('fr-FR', { maximumFractionDigits: 0 })} €
                        </span>
                      )}
                      {typeof it.expense === 'number' && (
                        <span className="ml-2 inline-flex items-center gap-1 text-rose-600 dark:text-rose-400">
                          <TrendingDown className="w-3 h-3"/> -{Math.abs(it.expense).toLocaleString('fr-FR', { maximumFractionDigits: 0 })} €
                        </span>
                      )}
                    </div>
                  </div>
                  {it.is_current && <Badge variant="secondary">{t('statements.inProgress')}</Badge>}
                  <Button onClick={() => download(it)} disabled={downloading === key} className="bg-blue-700 hover:bg-blue-800" data-testid={`btn-download-${key}`}>
                    {downloading === key ? <><Loader2 className="w-4 h-4 animate-spin mr-1"/> ...</> : <><Download className="w-4 h-4 mr-1"/> PDF</>}
                  </Button>
                </li>
              );
            })}
          </ul>
        )}
      </div>

      <div className="text-xs text-slate-500 dark:text-slate-400 bg-slate-50 dark:bg-slate-800/40 border border-slate-200 dark:border-slate-700 rounded-xl p-3">
        💡 {t('statements.archiveNote')}
      </div>
    </div>
  );
}
